import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "@/App.css";

const LegalLayout = ({ title, updated, children, testId }) => (
  <div className="page-root" data-testid={testId}>
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
          <img src="/premier-logo.png" alt="Premier Handling Solutions" className="h-8 w-auto" />
        </Link>
        <nav className="flex items-center gap-5 text-sm font-medium">
          <Link to="/" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#00599c] transition-colors">
            <ArrowLeft size={15} /> Home
          </Link>
          <Link to="/docs" className="text-[#00599c] hover:text-[#00477d] transition-colors">Docs</Link>
        </nav>
      </div>
    </header>
    <main className="mx-auto max-w-4xl px-6 py-12 pb-24">
      <p className="heading-font text-xs font-bold uppercase tracking-[0.2em] text-[#00599c]">PHS Pallets MCP Gateway</p>
      <h1 className="heading-font mt-3 text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">Last updated: {updated}</p>
      <div className="legal-body mt-8">{children}</div>
    </main>
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-6 py-5 text-sm text-slate-500">
        <span>© {new Date().getFullYear()} PHS Pallets · Premier Handling Solutions</span>
        <span className="flex gap-4">
          <Link to="/privacy" className="hover:text-[#00599c]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#00599c]">Terms of Service</Link>
        </span>
      </div>
    </footer>
  </div>
);

const H = ({ children }) => <h2 className="heading-font mt-10 text-xl font-bold tracking-tight text-slate-900">{children}</h2>;
const P = ({ children }) => <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{children}</p>;
const UL = ({ items }) => (
  <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed text-slate-600">
    {items.map((i, k) => <li key={k}>{i}</li>)}
  </ul>
);

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testId="privacy-page">
      <P>
        This Privacy Policy describes how Premier Handling Solutions ("PHS," "we," "us") handles information
        in connection with the PHS Pallets MCP Gateway — including this website (mcp.phsinc.com), the MCP
        server at mcp.phsinc.com/api/mcp, and the PHS Pallets connector for AI assistants such as ChatGPT
        and Claude (together, the "Service").
      </P>

      <H>1. Information we collect</H>
      <P>The Service is a stateless gateway: it operates no customer database of its own. Information passes through it only to fulfill your requests:</P>
      <UL items={[
        "Quote requests — when you request a pallet quote, we collect the contact details you provide: name, email address, company name (optional), destination ZIP code, requested items and any notes.",
        "Account sign-in — when a returning customer signs in, we process the account email address and a single-use 6-digit verification code sent to that email. We never collect passwords through the Service.",
        "Order and quote data — when signed in, the Service retrieves your own orders, quotes and shipment tracking details from PHS systems on your behalf.",
        "Website analytics — this website collects standard usage analytics (pages viewed, approximate location from IP, device/browser type, session interactions) to help us improve the site.",
        "Catalog queries — product searches and browsing requests are processed in real time and are not linked to an identity.",
      ]} />

      <H>2. How we use information</H>
      <UL items={[
        "To prepare and deliver pallet quotes, including freight estimates and follow-up by our sales team.",
        "To authenticate returning customers via emailed one-time codes and to show them their own orders, quotes and tracking information.",
        "To send transactional emails: quote confirmations, payment links, sign-in codes and order updates.",
        "To operate, secure and improve the Service.",
      ]} />
      <P>We do not use information collected through the Service for advertising, and we do not sell personal information.</P>

      <H>3. Storage and security</H>
      <P>
        The gateway itself stores nothing: every request is proxied over encrypted HTTPS/TLS to PHS production
        systems and no conversation content, tokens or personal data are retained on the gateway. Quote requests,
        customer accounts and order records are stored in PHS's order-management systems, protected by access
        controls and encryption in transit. Sign-in codes are stored only as hashes, are single-use, and expire
        after 10 minutes. Session tokens are cryptographically signed and short-lived.
      </P>

      <H>4. Third-party sharing</H>
      <P>We share information only as needed to run the Service:</P>
      <UL items={[
        "Freight carriers — shipping details (name, company, delivery address) needed to deliver your order.",
        "Stripe — payment processing when you choose to pay a quote. Payment always happens through Stripe checkout outside of any chat; the Service never sees card details.",
        "Email delivery providers — to send quote confirmations, payment links and sign-in codes.",
        "AI platforms — if you use the Service through ChatGPT (OpenAI) or Claude (Anthropic), your conversation content is handled by that platform under its own privacy policy. We receive only the specific tool requests the assistant sends to our server.",
        "Legal requirements — where required by law, regulation or valid legal process.",
      ]} />
      <P>We do not sell, rent or trade personal information, and we do not share it with third parties for their own marketing.</P>

      <H>5. Data retention</H>
      <UL items={[
        "Gateway: retains no personal data — requests are processed in memory and discarded.",
        "Sign-in codes: expire after 10 minutes and are deleted after use.",
        "Quotes and orders: retained in PHS business systems for as long as needed to service your account and to meet legal, tax and accounting obligations for commercial transactions.",
        "Website analytics: retained in aggregate form for service-improvement purposes.",
      ]} />
      <P>You may request access to, correction of, or deletion of your personal information using the contact details below. We will honor deletion requests except where retention is required by law.</P>

      <H>6. Children's privacy</H>
      <P>The Service is a business-to-business tool for purchasing industrial pallets and is not directed to children under 16. We do not knowingly collect information from children.</P>

      <H>7. Changes to this policy</H>
      <P>We may update this policy from time to time. Material changes will be reflected by a new "Last updated" date on this page.</P>

      <H>8. Contact information</H>
      <P>
        Premier Handling Solutions (PHS Pallets)<br />
        Headquarters: Elgin, Illinois, USA<br />
        Phone: <a href="tel:+18004042311" className="text-[#00599c] underline underline-offset-2">(800) 404-2311</a><br />
        Web: <a href="https://phsinc.com" target="_blank" rel="noopener noreferrer" className="text-[#00599c] underline underline-offset-2">phsinc.com</a> (contact form)
      </P>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026" testId="terms-page">
      <P>
        These Terms of Service ("Terms") govern use of the PHS Pallets MCP Gateway — this website
        (mcp.phsinc.com), the MCP server at mcp.phsinc.com/api/mcp, and the PHS Pallets connector for AI
        assistants (together, the "Service"), operated by Premier Handling Solutions ("PHS," "we," "us").
        By using the Service you agree to these Terms.
      </P>

      <H>1. The Service</H>
      <P>
        The Service lets you browse the PHS plastic pallet catalog, request quotes, and — for existing PHS
        customers — view orders, quotes and shipment tracking and create reorder quotes, including through AI
        assistants such as ChatGPT and Claude.
      </P>

      <H>2. Quotes and orders</H>
      <UL items={[
        "Quotes generated through the Service, including freight estimates and lead times, are estimates and are not binding until confirmed by the PHS sales team.",
        "Prices, stock levels and product availability reflect live data at the time of the request and may change without notice.",
        "No payment is ever collected through the Service or inside a chat conversation. Payment occurs exclusively via secure Stripe checkout through an emailed link or the PHS Pallets mobile app.",
        "All sales are subject to PHS's standard sales terms provided with your quote or invoice.",
      ]} />

      <H>3. Accounts and sign-in</H>
      <UL items={[
        "Customer sign-in uses single-use verification codes emailed to the account address. You are responsible for maintaining control of that email account.",
        "Never share verification codes. PHS will never ask for a password through the Service.",
        "Sessions are limited in scope: they permit viewing your own orders and quotes and creating reorder quotes only. Nothing can be modified or deleted through the Service.",
      ]} />

      <H>4. Acceptable use</H>
      <P>You agree not to:</P>
      <UL items={[
        "Attempt to access another customer's data or probe, scan or test the Service for vulnerabilities without authorization.",
        "Use the Service to submit false, misleading or fraudulent quote requests.",
        "Interfere with the operation of the Service, including through excessive automated requests.",
        "Resell or redistribute Service data in a way that misrepresents its source.",
      ]} />

      <H>5. AI assistants</H>
      <P>
        When you access the Service through a third-party AI platform, that platform's own terms and privacy
        policy also apply to your conversation. AI-generated summaries of our data may contain errors — the
        authoritative product specifications, pricing and order information are those returned by the Service
        and confirmed by PHS in writing.
      </P>

      <H>6. Intellectual property</H>
      <P>
        The Service, including its content, branding and documentation, is owned by PHS or its licensors.
        You may use it only as needed to evaluate and purchase PHS products.
      </P>

      <H>7. Disclaimers and limitation of liability</H>
      <P>
        The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
        To the maximum extent permitted by law, PHS will not be liable for indirect, incidental, special or
        consequential damages arising from use of the Service. PHS's total liability in connection with the
        Service will not exceed the amounts you paid to PHS for the products giving rise to the claim.
      </P>

      <H>8. Changes and termination</H>
      <P>
        We may modify or discontinue the Service, or update these Terms, at any time. Continued use after
        changes take effect constitutes acceptance. We may suspend access that violates these Terms.
      </P>

      <H>9. Governing law</H>
      <P>These Terms are governed by the laws of the State of Illinois, USA, without regard to conflict-of-law principles.</P>

      <H>10. Contact</H>
      <P>
        Premier Handling Solutions (PHS Pallets) · Elgin, Illinois, USA<br />
        Phone: <a href="tel:+18004042311" className="text-[#00599c] underline underline-offset-2">(800) 404-2311</a> ·{" "}
        <a href="https://phsinc.com" target="_blank" rel="noopener noreferrer" className="text-[#00599c] underline underline-offset-2">phsinc.com</a>
      </P>
    </LegalLayout>
  );
}
